import React, { useEffect, useState } from "react";
import { dms } from "./api";
import OwnerInsightsV2 from "./OwnerInsightsV2";
import OwnerMoreV2 from "./OwnerMoreV2";

const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const num = (n, d = 0) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: d });
const box = { background: "var(--card, #fff)", border: "1px solid rgba(128,128,128,0.25)", borderRadius: 14, padding: 16 };
const PERIODS = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["mtd", "This month"], ["fy", "FY"]];
const SEV = { high: ["#b91c1c", "rgba(220,38,38,0.10)"], mid: ["#b45309", "rgba(245,158,11,0.13)"], info: ["#475569", "rgba(100,116,139,0.12)"] };
const LABEL = { pending: "Pending", partially_fulfilled: "Partial", ready_to_go: "Ready to go", received: "Received", invoiced: "Invoiced", dispatched: "Dispatched", cancelled: "Cancelled" };

function Delta({ cur, prev }) {
  if (!prev) return null;
  const pct = ((cur - prev) / prev) * 100;
  const up = pct >= 0;
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: up ? "#15803d" : "#b91c1c" }}>
      {up ? "▲" : "▼"} {Math.abs(pct).toFixed(0)}% <span style={{ fontWeight: 400, opacity: 0.6 }}>vs previous period</span>
    </div>
  );
}

function Kpi({ label, value, sub, children }) {
  return (
    <div style={{ ...box, minWidth: 0 }}>
      <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", opacity: 0.6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, margin: "6px 0 2px" }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, opacity: 0.65 }}>{sub}</div> : null}
      {children}
    </div>
  );
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtK = (k) => {
  if (!k) return "";
  if (k.length === 7) return MON[Number(k.slice(5, 7)) - 1] + " " + k.slice(0, 4);
  return k.slice(8, 10) + " " + MON[Number(k.slice(5, 7)) - 1];
};
const shortInr = (n) => {
  n = Number(n || 0);
  if (n >= 10000000) return "₹" + (n / 10000000).toFixed(1) + " Cr";
  if (n >= 100000) return "₹" + (n / 100000).toFixed(1) + " lakh";
  if (n >= 1000) return "₹" + Math.round(n / 1000) + "K";
  return "₹" + Math.round(n);
};
const niceMax = (m) => {
  const p = Math.pow(10, Math.floor(Math.log10(m)));
  for (const f of [1, 2, 2.5, 5, 10]) { if (f * p >= m) return f * p; }
  return 10 * p;
};

function Trend({ rows }) {
  const [hover, setHover] = useState(null);
  const [hide, setHide] = useState({ p: false, s: false });
  if (!rows || rows.length < 2) return <div style={{ opacity: 0.6, fontSize: 13 }}>Not enough data for a trend yet</div>;
  const SER = [
    { key: "p", name: "Primary", note: "Owner to Distributors", color: "#b8860b" },
    { key: "s", name: "Secondary", note: "Distributors to Retailers", color: "#2563eb" },
  ];
  const W = 600, H = 220, n = rows.length;
  const seen = Math.max(1, ...rows.map((r) => Math.max(hide.p ? 0 : r.p, hide.s ? 0 : r.s)));
  const top = niceMax(seen);
  const x = (i) => (i * W) / (n - 1);
  const y = (v) => H - (v / top) * H;
  const pts = (key) => rows.map((r, i) => x(i) + "," + y(r[key])).join(" ");
  const total = (key) => rows.reduce((a, r) => a + r[key], 0);
  const best = (key) => rows.reduce((b, r) => (r[key] > b[key] ? r : b), rows[0]);
  const activeDays = rows.filter((r) => r.p > 0 || r.s > 0).length;
  const unit = rows[0].k.length === 7 ? "months" : "days";
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const steps = Math.min(5, n - 1);
  const labelIdx = [];
  for (let i = 0; i <= steps; i++) labelIdx.push(Math.round((i * (n - 1)) / steps));
  const move = (clientX, el) => {
    const rect = el.getBoundingClientRect();
    const rel = (clientX - rect.left) / rect.width;
    setHover(Math.max(0, Math.min(n - 1, Math.round(rel * (n - 1)))));
  };
  const hr = hover === null ? null : rows[hover];
  const hpct = hover === null ? 0 : (hover / (n - 1)) * 100;

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        {SER.map((s) => (
          <button key={s.key} onClick={() => setHide({ ...hide, [s.key]: !hide[s.key] })}
            style={{ textAlign: "left", cursor: "pointer", padding: "8px 12px", borderRadius: 10, border: "1px solid rgba(128,128,128,0.3)", borderLeft: "4px solid " + s.color, background: "transparent", color: "inherit", opacity: hide[s.key] ? 0.4 : 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{s.name} <span style={{ fontWeight: 400, opacity: 0.65 }}>({s.note})</span></div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{inr(total(s.key))}</div>
          </button>
        ))}
        <div style={{ alignSelf: "center", fontSize: 12, opacity: 0.6 }}>Click a box to hide or show its line</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ position: "relative", width: 76, height: H, flexShrink: 0 }}>
          {ticks.map((t) => (
            <div key={t} style={{ position: "absolute", right: 0, top: (1 - t) * H, transform: "translateY(-50%)", fontSize: 11, opacity: 0.65 }}>{shortInr(top * t)}</div>
          ))}
        </div>
        <div style={{ position: "relative", flex: 1, height: H, cursor: "crosshair" }}
          onMouseMove={(e) => move(e.clientX, e.currentTarget)} onMouseLeave={() => setHover(null)}
          onTouchMove={(e) => move(e.touches[0].clientX, e.currentTarget)}>
          <svg viewBox={"0 0 " + W + " " + H} preserveAspectRatio="none" style={{ width: "100%", height: H, display: "block", overflow: "visible" }}>
            {ticks.map((t) => (
              <line key={t} x1="0" x2={W} y1={H - t * H} y2={H - t * H} stroke="currentColor" opacity={t === 0 ? 0.4 : 0.12} vectorEffect="non-scaling-stroke" />
            ))}
            {SER.map((s) => hide[s.key] ? null : (
              <g key={s.key}>
                <polygon points={"0," + H + " " + pts(s.key) + " " + W + "," + H} fill={s.color} opacity="0.08" />
                <polyline points={pts(s.key)} fill="none" stroke={s.color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
              </g>
            ))}
            {hover !== null ? <line x1={x(hover)} x2={x(hover)} y1="0" y2={H} stroke="currentColor" opacity="0.35" vectorEffect="non-scaling-stroke" /> : null}
          </svg>
          {hr ? SER.map((s) => hide[s.key] ? null : (
            <div key={s.key} style={{ position: "absolute", left: hpct + "%", top: (y(hr[s.key]) / H) * 100 + "%", width: 10, height: 10, borderRadius: 999, background: s.color, border: "2px solid #fff", transform: "translate(-50%,-50%)", pointerEvents: "none" }} />
          )) : null}
          {hr ? (
            <div style={{ position: "absolute", top: 4, left: hpct + "%", transform: hpct > 60 ? "translateX(-105%)" : "translateX(10px)", background: "var(--card, #fff)", border: "1px solid rgba(128,128,128,0.35)", borderRadius: 10, padding: "8px 12px", fontSize: 12, boxShadow: "0 4px 14px rgba(0,0,0,0.12)", pointerEvents: "none", whiteSpace: "nowrap" }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{fmtK(hr.k)}</div>
              <div><span style={{ color: "#b8860b" }}>●</span> Primary: <b>{inr(hr.p)}</b></div>
              <div><span style={{ color: "#2563eb" }}>●</span> Secondary: <b>{inr(hr.s)}</b></div>
            </div>
          ) : null}
        </div>
      </div>
      <div style={{ position: "relative", height: 18, marginLeft: 84, marginTop: 6 }}>
        {labelIdx.map((i) => (
          <div key={i} style={{ position: "absolute", left: (i / (n - 1)) * 100 + "%", transform: i === 0 ? "none" : i === n - 1 ? "translateX(-100%)" : "translateX(-50%)", fontSize: 11, opacity: 0.65, whiteSpace: "nowrap" }}>{fmtK(rows[i].k)}</div>
        ))}
      </div>
      <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: "rgba(128,128,128,0.07)", fontSize: 13, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>What this chart tells you</div>
        <div>Gold line (Primary) = bills you made to distributors. Blue line (Secondary) = bills distributors made to retailers.</div>
        <div>Primary total: <b>{inr(total("p"))}</b>. Best day: <b>{fmtK(best("p").k)}</b> ({inr(best("p").p)}).</div>
        <div>Secondary total: <b>{inr(total("s"))}</b>. Best day: <b>{fmtK(best("s").k)}</b> ({inr(best("s").s)}).</div>
        <div>Sales happened on <b>{activeDays}</b> of {n} {unit}.{total("s") > total("p") ? " Retailers bought more from distributors than distributors bought from you, so distributor stock may be going down." : total("p") > total("s") ? " Distributors bought more from you than they sold to retailers, so their stock may be building up." : ""}</div>
      </div>
    </div>
  );
}

function Chips({ title, data }) {
  const keys = Object.keys(data || {});
  return (
    <div style={box}>
      <div style={{ fontWeight: 600, marginBottom: 10 }}>{title}</div>
      {keys.length === 0 ? <div style={{ opacity: 0.6, fontSize: 13 }}>No orders</div> : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {keys.map((k) => (
            <div key={k} style={{ border: "1px solid rgba(128,128,128,0.3)", borderRadius: 999, padding: "4px 12px", fontSize: 13 }}>
              {LABEL[k] || k}: <b>{data[k]}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OwnerDashboardV2() {
  const [period, setPeriod] = useState("30d");
  const [distId, setDistId] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setErr(false);
    dms.ownerDashboardV2({ period: period, distributor_id: distId })
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [period, distId]);

  const k = (data && data.kpis) || {};
  const st = (data && data.stock) || {};
  const alerts = (data && data.alerts) || [];

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Business Pulse</div>
          <div style={{ fontSize: 13, opacity: 0.65 }}>{data ? data.from + " to " + data.to : "Loading..."}</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {PERIODS.map((p) => (
            <button key={p[0]} onClick={() => setPeriod(p[0])}
              style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontSize: 13, border: "1px solid rgba(128,128,128,0.35)", background: period === p[0] ? "#b8860b" : "transparent", color: period === p[0] ? "#fff" : "inherit" }}>
              {p[1]}
            </button>
          ))}
          <select value={distId} onChange={(e) => setDistId(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 8, fontSize: 13, border: "1px solid rgba(128,128,128,0.35)", background: "transparent", color: "inherit" }}>
            <option value="">All distributors</option>
            {((data && data.distributors) || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>

      {err ? <div style={{ ...box, color: "#b91c1c" }}>Could not load data. Please refresh the page.</div> : null}

      {data ? (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={box}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Needs Attention</div>
            {alerts.length === 0 ? (
              <div style={{ color: "#15803d", fontSize: 14 }}>All good. No pending alerts.</div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {alerts.map((a) => (
                  <div key={a.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, background: SEV[a.sev][1] }}>
                    <div style={{ minWidth: 34, textAlign: "center", fontWeight: 700, fontSize: 18, color: SEV[a.sev][0] }}>{a.count}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{a.title}</div>
                      {a.hint ? <div style={{ fontSize: 12, opacity: 0.7 }}>{a.hint}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 }}>
            <Kpi label="Primary Sales" value={inr(k.primary_sales)} sub={k.primary_bills + " e-bills (Owner to Distributor)"}><Delta cur={k.primary_sales} prev={k.primary_sales_prev} /></Kpi>
            <Kpi label="Secondary Sales" value={inr(k.secondary_sales)} sub={k.secondary_bills + " bills (Distributor to Retailer)"}><Delta cur={k.secondary_sales} prev={k.secondary_sales_prev} /></Kpi>
            <Kpi label="Litres" value={num(k.litres_secondary, 0) + " L"} sub={"Sold to retailers | To distributors: " + num(k.litres_primary, 0) + " L"} />
            <a href="/dms/owner/collections" style={{ textDecoration: "none", color: "inherit", display: "block" }}><Kpi label="Collection" value={inr(k.collected)} sub="Payments received (click to view and manage)" /></a>
            <Kpi label="Distributor Outstanding" value={inr(k.outstanding_primary)} sub="Primary invoices minus payments" />
            <Kpi label="Retailer Outstanding" value={inr(k.outstanding_retailers)} sub="Balance due on all retailer bills" />
            <Kpi label="Owner Stock Value" value={inr(k.inventory_value)} sub={st.owner_negative_count ? st.owner_negative_count + " products negative (not counted)" : "Positive stock only"} />
          </div>

          <div style={box}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
              <div><div style={{ fontWeight: 600 }}>Sales trend</div><div style={{ fontSize: 12, opacity: 0.6 }}>Money billed each day. Move your mouse over the chart to see exact amounts.</div></div>
            </div>
            <Trend rows={data.trend} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16 }}>
            <Chips title="Primary orders status" data={data.primary_status} />
            <Chips title="Retailer orders status" data={data.secondary_status} />
          </div>

          {(st.owner_negative_count > 0 || st.dist_negative_count > 0) ? (
            <div style={box}>
              <div style={{ fontWeight: 600, marginBottom: 10 }}>Stock shortfall (negative products)</div>
              {(st.owner_negative || []).map((r, i) => (
                <div key={"o" + i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                  <span>Owner: {r.name}</span><b style={{ color: "#b91c1c" }}>{r.qty} box</b>
                </div>
              ))}
              {(st.dist_negative || []).map((r, i) => (
                <div key={"d" + i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                  <span>{r.distributor}: {r.name}</span><b style={{ color: "#b91c1c" }}>{r.qty} box</b>
                </div>
              ))}
              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>Showing the top 6 most negative products. This will fix itself once the owner's opening stock is entered.</div>
            </div>
          ) : null}
        </div>
      ) : null}
      {data ? (
        <div style={{ marginTop: 16, display: "grid", gap: 16 }}>
          <OwnerInsightsV2 period={period} distId={distId} />
          <OwnerMoreV2 period={period} distId={distId} />
        </div>
      ) : null}
    </div>
  );
}
// END_OWNER_V2
